import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Power, AlertTriangle, Save, Activity, ShieldCheck } from "lucide-react";
import DryRunModal from "@/components/DryRunModal";

export default function AdminMonetization() {
  const [flags, setFlags] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [edits, setEdits] = useState({});
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [dryRunOpen, setDryRunOpen] = useState(false);
  const [sysStatus, setSysStatus] = useState(null);

  const load = async () => {
    try {
      const [f, c, s] = await Promise.all([
        api.get("/billing/flags"),
        api.get("/billing/admin/companies"),
        api.get("/admin/system-status").catch(() => ({ data: null })),
      ]);
      setFlags(f.data.flags); setCompanies(c.data); setEdits({});
      if (s.data) setSysStatus(s.data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const save = async (key, value) => {
    setErr(""); setMsg("");
    try { await api.post("/billing/flags", { key, value }); setMsg(`Updated ${key}`); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const toggleCompany = async (cid, enabled) => {
    await api.post(`/billing/admin/companies/${cid}/monetization`, { enabled });
    load();
  };
  const setOverride = async (cid, override, plan_tier = null) => {
    const reason = override ? (window.prompt("Reason for override?") || "Admin override") : null;
    if (override && !reason) return;
    await api.post(`/billing/admin/companies/${cid}/override`, { override, plan_tier, reason });
    load();
  };

  if (!flags) return <div style={{ color: "var(--ink-mute)" }}>Loading…</div>;

  const FLAG_BOOLEANS = ["monetization_enabled", "billing_enforced", "subscription_required"];
  const FLAG_NUMBERS = ["free_plan_rfq_quota", "pro_plan_rfq_quota", "enterprise_plan_rfq_quota"];

  const dormant = !flags.monetization_enabled;

  return (
    <>
      <PageHeader title="Monetization Controls"
        subtitle="Master switches and per-company controls. Everything is OFF by default and dormant — flip the master switch when you're ready to launch billing."
        actions={
          <button className="btn btn-ghost" data-testid="open-dryrun" onClick={() => setDryRunOpen(true)}>
            <Activity size={14} /> Pre-launch Dry-Run
          </button>
        } />

      <DryRunModal open={dryRunOpen} onClose={() => setDryRunOpen(false)} />

      <div className="card" style={{ padding: 20, marginBottom: 24, borderLeft: `3px solid ${dormant ? "var(--amber-500)" : "var(--emerald)"}`, display: "flex", gap: 14, alignItems: "center" }}>
        {dormant ? <AlertTriangle size={20} color="var(--amber-500)" /> : <Power size={20} color="var(--emerald)" />}
        <div>
          <div style={{ fontWeight: 600 }}>{dormant ? "Monetization is DORMANT" : "Monetization is LIVE"}</div>
          <div style={{ color: "var(--ink-dim)", fontSize: 13, marginTop: 4 }}>
            {dormant
              ? "All billing logic is built and tested but not enforcing anything. No quotas, no commissions, no subscription gates."
              : "Master switch is ON. Sub-flags below decide which gates are active."}
          </div>
        </div>
        <button className={dormant ? "btn btn-primary" : "btn btn-danger"}
                style={{ marginLeft: "auto" }}
                data-testid="toggle-monetization"
                onClick={() => save("monetization_enabled", !flags.monetization_enabled)}>
          <Power size={14} /> {dormant ? "Activate monetization" : "Deactivate monetization"}
        </button>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 24, borderLeft: "3px solid var(--ink-mute)", display: "flex", gap: 12, alignItems: "flex-start" }} data-testid="platform-vs-trade-banner">
        <ShieldCheck size={18} color="var(--ink-mute)" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>
          <b style={{ color: "var(--ink)" }}>Platform fees are SaaS-only.</b> Subscription billing here is paid TO the platform.
          Buyer↔supplier trade payments are settled <b>off-platform</b> — the platform never holds, escrows, splits, or remits money.
          The legacy commission flow is deprecated and will not be enabled.
        </div>
      </div>

      {sysStatus && (
        <div data-testid="system-status-grid" className="card" style={{ padding: 16, marginBottom: 24 }}>
          <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-mute)", marginBottom: 10 }}>SYSTEM STATUS</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            <Badge label="Email" value={sysStatus.email_mode === "live_resend" ? "LIVE (RESEND)" : "DEV-LOG"}
                   tone={sysStatus.email_mode === "live_resend" ? "ok" : "warn"}
                   sub={sysStatus.sender_email || "—"} testId="badge-email" />
            <Badge label="Stripe" value={sysStatus.stripe_mode === "webhook_ready" ? "WEBHOOK READY" : "DORMANT"}
                   tone={sysStatus.stripe_mode === "webhook_ready" ? "ok" : "muted"}
                   sub={sysStatus.monetization_enabled ? "billing enabled" : "billing OFF (intended)"} testId="badge-stripe" />
            <Badge label="Monetization" value={sysStatus.monetization_enabled ? "LIVE" : "DORMANT"}
                   tone={sysStatus.monetization_enabled ? "live" : "muted"}
                   sub={sysStatus.monetization_enabled ? "users see plans" : "no user impact"} testId="badge-monetization" />
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 20, marginBottom: 24 }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)", marginBottom: 14 }}>SUB-FLAGS</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
          {FLAG_BOOLEANS.filter(k => k !== "monetization_enabled").map(k => (
            <label key={k} className="card" style={{ padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{k.replace(/_/g, " ")}</div>
                <div style={{ fontSize: 12, color: "var(--ink-mute)", marginTop: 2 }}>Default: {String(false)}</div>
              </div>
              <input type="checkbox" data-testid={`flag-${k}`} checked={!!flags[k]} onChange={e => save(k, e.target.checked)} />
            </label>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginTop: 18 }}>
          {FLAG_NUMBERS.map(k => (
            <div key={k}>
              <label className="label">{k.replace(/_/g, " ")}</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input className="input" type="number" step="any"
                  defaultValue={flags[k]}
                  onChange={e => setEdits(v => ({ ...v, [k]: e.target.value }))} />
                <button className="btn btn-ghost" onClick={() => save(k, parseFloat(edits[k] ?? flags[k]))}><Save size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)" }} className="font-mono">
          <span style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)" }}>PER-COMPANY MONETIZATION · {companies.length}</span>
        </div>
        {companies.length === 0 ? <div style={{ padding: 32, textAlign: "center", color: "var(--ink-mute)" }}>No companies.</div> : (
          <table className="data">
            <thead><tr><th>Company</th><th>Role</th><th>Plan</th><th>Status</th><th>Tenant Enabled</th><th>Override</th><th>Actions</th></tr></thead>
            <tbody>
              {companies.map(c => {
                const sub = c.subscription || {};
                const tenantEnabled = c.monetization_enabled_for_tenant !== false;
                return (
                  <tr key={c.id}>
                    <td><div style={{ fontWeight: 600 }}>{c.legal_name}</div></td>
                    <td style={{ color: "var(--ink-dim)" }}>{c.role}</td>
                    <td><span className="pill" style={{ borderColor: "var(--line)", color: "var(--amber-400)" }}>{(sub.plan_tier || "free").toUpperCase()}</span></td>
                    <td>{sub.status || "inactive"}</td>
                    <td>
                      <label style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <input type="checkbox" checked={tenantEnabled} onChange={e => toggleCompany(c.id, e.target.checked)} />
                        <span style={{ fontSize: 12, color: tenantEnabled ? "var(--emerald)" : "var(--ink-mute)" }}>{tenantEnabled ? "ENABLED" : "EXEMPT"}</span>
                      </label>
                    </td>
                    <td>{sub.manual_override ? <span className="pill pill-accepted">YES</span> : "—"}</td>
                    <td style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {!sub.manual_override
                        ? <button className="btn btn-ghost" onClick={() => setOverride(c.id, true, "pro")} style={{ padding: "4px 10px", fontSize: 11 }}>Grant override</button>
                        : <button className="btn btn-danger" onClick={() => setOverride(c.id, false, null)} style={{ padding: "4px 10px", fontSize: 11 }}>Revoke override</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {err && <div style={{ color: "var(--red)", marginTop: 12 }}>{err}</div>}
      {msg && <div style={{ color: "var(--emerald)", marginTop: 12 }}>{msg}</div>}
    </>
  );
}

function Badge({ label, value, tone, sub, testId }) {
  const colors = {
    ok: "var(--emerald)",
    warn: "var(--amber-500)",
    live: "#3b82f6",
    muted: "var(--ink-mute)",
  };
  return (
    <div data-testid={testId} style={{ padding: "12px 14px", background: "rgba(255,255,255,.02)", borderLeft: `3px solid ${colors[tone] || "var(--ink-mute)"}`, borderRadius: 2 }}>
      <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--ink-mute)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4, color: colors[tone] }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--ink-mute)", marginTop: 2 }}>{sub}</div>
    </div>
  );
}
